import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { FileSignature, Loader2, ShieldAlert } from 'lucide-react';
import type { Project } from '../types';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { useApp } from '../providers/AppProvider';
import { useAuth } from '../providers/AuthProvider';
import { updateProject } from '../store';
import { setProjectNdaSignatures } from '../services/dataService';
import { toast } from 'sonner';
import {
  getEffectiveProjectNdaSettings,
  getProjectNdaTextSections,
  upsertProjectNdaSignature,
} from '../utils/projectNda';
import { createAndUploadProjectNdaSignature } from '../services/projectNdaService';

interface ProjectNdaGateProps {
  project: Project;
  organizationName: string;
}

export function ProjectNdaGate({ project, organizationName }: ProjectNdaGateProps) {
  const navigate = useNavigate();
  const { state, setState } = useApp();
  const { firebaseUser } = useAuth();
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUser = state?.currentUser || null;
  const ndaSettings = useMemo(() => getEffectiveProjectNdaSettings(project), [project]);
  const ndaSections = useMemo(
    () => getProjectNdaTextSections(project, organizationName),
    [organizationName, project]
  );

  const handleSign = async () => {
    if (!currentUser) {
      toast.error('User context is not loaded yet.');
      return;
    }
    if (!acknowledged) {
      toast.error('You must check the acknowledgement box before signing.');
      return;
    }

    setIsSubmitting(true);
    try {
      const signature = await createAndUploadProjectNdaSignature({
        project,
        organizationName,
        user: currentUser,
        authUid: firebaseUser?.uid,
        googleDisplayName: firebaseUser?.displayName,
        googleEmail: firebaseUser?.email,
      });

      const nextSignatures = upsertProjectNdaSignature(project.ndaSignatures, signature);
      await setProjectNdaSignatures(project.organizationId, project.id, nextSignatures);
      await updateProject(project.id, {
        ndaSettings,
        updatedAt: signature.signedAt,
      });

      setState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          projects: (prev.projects || []).map((p) =>
            p.id === project.id
              ? { ...p, ndaSettings, ndaSignatures: nextSignatures, updatedAt: signature.signedAt }
              : p
          ),
        };
      });

      toast.success('NDA signed. Welcome to the project.');
    } catch (err) {
      console.error('[ProjectNdaGate] sign error:', err);
      toast.error('Failed to save the NDA signature. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/40 px-4 py-10">
      <div className="mx-auto max-w-4xl space-y-6">
        <Card className="border-chart-5/40 bg-chart-5/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-chart-5">
              <ShieldAlert className="h-5 w-5" />
              NDA Required Before Project Access
            </CardTitle>
            <CardDescription className="text-chart-5">
              Please read and sign the project Non-Disclosure Agreement before accessing{' '}
              <strong>{project.name}</strong>. You only need to sign once.
            </CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" />
              Project Non-Disclosure Agreement
            </CardTitle>
            <CardDescription>
              Template version: {ndaSettings.standardTemplateVersion}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-lg border border-border bg-white p-4 max-h-[420px] overflow-y-auto space-y-4">
              {ndaSections.map((section, index) => {
                const isHeading = section === 'Project-specific additional clauses:';
                const isLead = index === 0;
                return (
                  <p
                    key={`${index}-${section.slice(0, 20)}`}
                    className={
                      isHeading
                        ? 'text-sm font-semibold text-muted-foreground'
                        : isLead
                        ? 'text-sm text-muted-foreground leading-6'
                        : 'text-sm text-muted-foreground leading-6'
                    }
                  >
                    {isHeading || isLead ? section : `• ${section}`}
                  </p>
                );
              })}
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4">
              <Checkbox
                id="nda-ack"
                checked={acknowledged}
                onCheckedChange={(v) => setAcknowledged(v === true)}
              />
              <Label htmlFor="nda-ack" className="cursor-pointer text-sm leading-5 text-muted-foreground">
                I have read and understood the Non-Disclosure Agreement above. I agree to be bound
                by its terms and acknowledge that all project artifacts belong to the organization.
              </Label>
            </div>

            <div className="flex items-center justify-between gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/app/projects')}
                disabled={isSubmitting}
              >
                Back to Projects
              </Button>
              <Button onClick={handleSign} disabled={!acknowledged || isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <FileSignature className="mr-2 h-4 w-4" />
                    Sign &amp; Enter Project
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
